import React, { useEffect, useState } from "react";
import axios from "axios";
import { SketchPicker } from "react-color";
import { AiOutlinePlusCircle } from "react-icons/ai";
import { Icon } from "organisms/iconCommonent";
import { MdDelete } from "react-icons/md";

interface PartitionCategory {
  label: string;
  percentage: number;
  color: string;
}

interface Category {
  title: string;
  items: string[];
  color: string;
}

interface ChartType {
  _id: string;
  heading: string;
  partioncategory: PartitionCategory[];
  categories: Category[];
}

export default function SectorAdmin() {
  const [charts, setCharts] = useState<ChartType[]>([]);
  const [editingChart, setEditingChart] = useState<ChartType | null>(null);

  // Fetch charts
  const fetchCharts = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_LOCAL_URL}/api/sector-of-operation`
      );
      setCharts(res.data.message);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchCharts();
  }, []);

  // Handle chart edit
  const handleEdit = (chart: ChartType) => {
    setEditingChart({ ...chart }); // make a copy for editing
  };

  // Handle update
  const handleUpdate = async () => {
    if (!editingChart) return;
    try {
      await axios.patch(
        `${process.env.REACT_APP_LOCAL_URL}/api/sector-of-operation/${editingChart._id}`,
        editingChart
      );
      setEditingChart(null);
      fetchCharts();
    } catch (err) {
      console.error(err);
      alert("Failed to update chart");
    }
  };

  // Update partition
  const updatePartition = (
    index: number,
    field: "percentage" | "color",
    value: any
  ) => {
    if (!editingChart) return;

    const updated = [...editingChart.partioncategory];

    // Type assertion
    updated[index] = {
      ...updated[index],
      [field]: value,
    };

    setEditingChart({ ...editingChart, partioncategory: updated });
  };

  // Update category
  const updateCategory = (
    index: number,
    field: "title" | "color" | "items",
    value: any
  ) => {
    if (!editingChart) return;
    const updated = [...editingChart.categories];
    updated[index][field] = value;
    setEditingChart({ ...editingChart, categories: updated });
  };

  // Add new category
  const addCategory = () => {
    if (!editingChart) return;
    const updated = [
      ...editingChart.categories,
      { title: "", items: [""], color: "#0f766e" },
    ];
    setEditingChart({ ...editingChart, categories: updated });
  };

  // Delete category
  const deleteCategory = (index: number) => {
    if (!editingChart) return;
    const updated = [...editingChart.categories];
    updated.splice(index, 1);
    setEditingChart({ ...editingChart, categories: updated });
  };

  return (
    <div className="p-6  min-h-screen">
      <h2 className=" mb-6">Section-09 (Sectors of Operation)</h2>

      {/* Existing Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white border p-4 border-gray-200 rounded-xl overflow-hidden">
        {charts.map((chart) => (
          <div
            key={chart._id}
            className="bg-white p-5 rounded-xl shadow-md relative"
          >
            <h3 className="text-xl font-lato-regular text-[#07393C] mb-2">
              {chart.heading}
            </h3>

            <div className="mb-3">
              <h4 className=" text-gray-600 font-lato-regular py-2">
                Partitions:
              </h4>

              <ul className="ml-4 list-disc font-lato-regular">
                {chart.partioncategory.map((p, idx) => (
                  <li key={idx}>
                    {p.label} – {p.percentage}%{" "}
                    <span
                      className="inline-block w-4 h-4 ml-2 rounded font-lato-regular"
                      style={{ backgroundColor: p.color }}
                    ></span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-600">Categories:</h4>
              {chart.categories.map((cat, idx) => (
                <div key={idx} className="mt-2">
                  <strong>{cat.title}</strong>
                  <ul className="ml-4 list-disc font-lato-regular">
                    {cat.items.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <button
              onClick={() => handleEdit(chart)}
              className="mt-3 bg-blue-600 text-white px-3 py-1 rounded"
            >
              Edit
            </button>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editingChart && (
        <div className="fixed inset-0 flex justify-center items-start pt-20 bg-black/40 z-50 overflow-auto">
          <div className="bg-white p-6 rounded-2xl w-[600px] shadow-lg mb-20">
            <label className="block font-semibold my-2">Heading</label>
            <input
              type="text"
              placeholder="Heading"
              value={editingChart.heading}
              onChange={(e) =>
                setEditingChart({ ...editingChart, heading: e.target.value })
              }
              className="border p-2 rounded w-full mb-3"
            />

            {editingChart.partioncategory.map((p, idx) => (
              <div key={idx} className="flex gap-4 items-center mb-2">
                <span className="w-1/3">{p.label}</span>
                <input
                  type="number"
                  value={p.percentage}
                  onChange={(e) =>
                    updatePartition(idx, "percentage", Number(e.target.value))
                  }
                  className="border p-2 rounded w-1/3"
                />
                <SketchPicker
                  color={p.color}
                  onChangeComplete={(color) =>
                    updatePartition(idx, "color", color.hex)
                  }
                />
              </div>
            ))}

            <h5 className="font-semibold mt-4">Categories</h5>
            {editingChart.categories.map((cat, idx) => (
              <div key={idx} className="mb-4 border p-3 rounded">
                <input
                  type="text"
                  placeholder="Category Title"
                  value={cat.title}
                  onChange={(e) => updateCategory(idx, "title", e.target.value)}
                  className="border p-2 rounded w-full mb-2"
                />
                <input
                  type="text"
                  placeholder="Items (comma separated)"
                  value={cat.items.join(", ")}
                  onChange={(e) =>
                    updateCategory(
                      idx,
                      "items",
                      e.target.value.split(",").map((i) => i.trim())
                    )
                  }
                  className="border p-2 rounded w-full mb-2"
                />
                <SketchPicker
                  color={cat.color}
                  onChangeComplete={(color) =>
                    updateCategory(idx, "color", color.hex)
                  }
                />
                <div className="mt-4">
                  <button
                    onClick={() => deleteCategory(idx)}
                    className="text-red-500 border border-red-500 px-2 py-2 rounded"
                  >
                    <Icon icon={MdDelete} />
                  </button>
                </div>
              </div>
            ))}

            <button
              className="bg-darkCyan mt-4 flex items-center text-white font-lato-regular rounded-md px-2 py-2"
              onClick={addCategory}
            >
              <span className="text-2xl pr-2">
                <Icon icon={AiOutlinePlusCircle} />
              </span>
              <h6>Add new category</h6>
            </button>

            <div className="flex justify-end gap-3 mt-4">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                onClick={handleUpdate}
              >
                Update
              </button>
              <button
                onClick={() => setEditingChart(null)}
                className="text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
